/**
 * One person: their record, their membership, and the two things §3.2 insists
 * are different edges.
 *
 * Membership and access are rendered as separate panels with the distinction
 * stated, because conflating them is "a modeling error that gets expensive
 * later" — and the cheapest place to keep them apart is the screen where
 * somebody would otherwise learn they are the same thing.
 *
 * Readable by every employee role, so every action here is gated on the
 * viewer's own capability rather than on the page being reachable. What is
 * editable from this screen is the *record* — a name and an email. A role is a
 * property of the account and moves in Settings → Users.
 *
 * **A person may have no login**, and this is still where one is given (phase 2
 * §2.5), because this is the screen somebody is on when they decide to. "Give
 * access" is not an edit to a field: it creates the account and sends the
 * invitation, which is why it is a distinct action with its own dialog rather
 * than a role dropdown that happens to start empty.
 */

import { Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { Form } from 'react-aria-components';
import {
  useCreateUser,
  useMe,
  usePerson,
  useResendInvitation,
  useUpdatePerson,
} from '../../api/queries.ts';
import { USER_ROLES, type UserRole } from '../../api/types.ts';
import { roleCarries } from '../../components/capabilities.ts';
import { formatDateTime, formatRelative } from '../../components/format.ts';
import { Badge, RoleBadge, roleLabel, StatusBadge } from '../../components/ui/badge.tsx';
import { Button, LinkButton } from '../../components/ui/button.tsx';
import { Dialog, DialogBody, DialogFooter } from '../../components/ui/dialog.tsx';
import { Alert, ErrorState, Loading, PageError } from '../../components/ui/feedback.tsx';
import { Select, TextField } from '../../components/ui/field.tsx';
import {
  Card,
  CardHeader,
  Definition,
  DefinitionList,
  Mono,
  PageHeader,
} from '../../components/ui/layout.tsx';

const ACCESS_ROLE_OPTIONS = USER_ROLES.map((role) => ({
  value: role,
  label: roleLabel(role),
  description:
    role === 'admin' || role === 'staff' || role === 'contractor'
      ? 'Staff side — signs in with Google Workspace SSO'
      : 'Client side — signs in with a magic link',
}));

/**
 * §2.5 — "give this person access". The role is chosen here rather than at
 * creation because a role is a property of the account, and until this dialog is
 * submitted there is no account for one to be a property of.
 */
function GiveAccessDialog({
  personId,
  name,
  onClose,
}: {
  personId: string;
  name: string;
  onClose: () => void;
}) {
  const create = useCreateUser();
  const [role, setRole] = useState<UserRole>('staff');

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate({ personId, role }, { onSuccess: onClose });
      }}
    >
      <DialogBody>
        <Select
          label="Role"
          options={ACCESS_ROLE_OPTIONS}
          selectedKey={role}
          onSelectionChange={setRole}
          description="Sets the sign-in path and the capability baseline. It grants nothing anywhere on its own — a grant still has to reach the node."
        />
        <Alert tone="warning" title="This sends an invitation">
          {name} gets an email the moment this is saved. For a magic-link role it carries their
          first link; for an SSO role it is informational. There is no quiet path to an account.
        </Alert>
        {create.isError ? <ErrorState error={create.error} /> : null}
      </DialogBody>
      <DialogFooter>
        <Button variant="tertiary" onPress={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" isPending={create.isPending}>
          Give access and invite
        </Button>
      </DialogFooter>
    </Form>
  );
}

/** The record. A role is the account's, and moves in Settings → Users. */
function EditDialog({
  personId,
  initial,
  onClose,
}: {
  personId: string;
  initial: { name: string; email: string };
  onClose: () => void;
}) {
  const update = useUpdatePerson(personId);
  const [name, setName] = useState(initial.name);
  const [email, setEmail] = useState(initial.email);

  const changed = name !== initial.name || email !== initial.email;

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
        update.mutate(
          {
            ...(name !== initial.name ? { name } : {}),
            ...(email !== initial.email ? { email } : {}),
          },
          { onSuccess: onClose },
        );
      }}
    >
      <DialogBody>
        <TextField label="Full name" value={name} onChange={setName} isRequired />
        <TextField
          label="Email address"
          type="email"
          value={email}
          onChange={setEmail}
          isRequired
          description="Changing this changes what SSO matches on."
        />
        {update.isError ? <ErrorState error={update.error} /> : null}
      </DialogBody>
      <DialogFooter>
        <Button variant="tertiary" onPress={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" isPending={update.isPending} isDisabled={!changed}>
          Save changes
        </Button>
      </DialogFooter>
    </Form>
  );
}

export function PersonDetailPage() {
  const { personId } = useParams({ from: '/_authed/_employee/people/$personId/' });
  const query = usePerson(personId);
  const resend = useResendInvitation(personId);
  const me = useMe();
  const canManage = me.data ? roleCarries(me.data.user.role, 'user.manage') : false;
  const canDeactivate = me.data ? roleCarries(me.data.user.role, 'user.deactivate') : false;
  const [editing, setEditing] = useState(false);
  const [givingAccess, setGivingAccess] = useState(false);

  if (query.isPending) return <Loading />;
  if (query.isError) {
    return (
      <PageError
        error={query.error}
        title="Person"
        back={{ href: '/people', label: 'Back to people' }}
      />
    );
  }

  const { person, memberships, grantCount } = query.data;

  return (
    <>
      <PageHeader
        title={person.name}
        description={person.email}
        actions={
          <>
            {canManage ? (
              <Button variant="secondary" onPress={() => setEditing(true)}>
                Edit
              </Button>
            ) : null}
            {canManage && person.account === null ? (
              <Button variant="primary" onPress={() => setGivingAccess(true)}>
                Give access
              </Button>
            ) : null}
            {canDeactivate && person.account !== null && person.account.status !== 'deactivated' ? (
              <LinkButton
                variant="destructive-secondary"
                href={`/settings/workspace/users/${person.id}/deactivate`}
              >
                Deactivate
              </LinkButton>
            ) : null}
          </>
        }
      />

      {person.account?.status === 'deactivated' ? (
        <Alert tone="warning" title="This person is deactivated">
          Their sessions were revoked and their person-specific grants deleted. Anything their{' '}
          <em>role</em> reaches is denied too — the resolver filters{' '}
          <span className="font-mono">status = 'active'</span> before it looks at a single grant, so
          there is nothing left to revoke. The record is kept because authorship, event history, and
          audit references have to survive.
        </Alert>
      ) : null}

      {person.account === null ? (
        <Alert tone="info" title="This person has no login">
          They are a record of a human — a contact, an approver named in a document, somebody a
          grant may already name. Nothing signs in as them, and nothing can: there is no account row
          for a session to reference, so every permission check denies before it reads a grant.
          "Give access" creates one and sends the invitation.
        </Alert>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <h2 className="mb-4 text-md font-semibold text-primary">Record</h2>
          <DefinitionList>
            {person.account ? (
              <>
                <Definition term="Role">
                  <RoleBadge role={person.account.role} />
                </Definition>
                <Definition term="Status">
                  <StatusBadge status={person.account.status} />
                </Definition>
                <Definition term="Sign-in method">
                  <Badge tone="outline" mono>
                    {person.account.authMethod}
                  </Badge>
                  <span className="ml-2 text-tertiary">
                    {person.account.authMethod === 'sso'
                      ? 'Google Workspace, domain-restricted'
                      : 'Magic link, single use, 15 minutes'}
                  </span>
                </Definition>
                <Definition term="Last seen">
                  {formatRelative(person.account.lastSeenAt)}
                  <span className="ml-2 text-tertiary">
                    {formatDateTime(person.account.lastSeenAt)}
                  </span>
                </Definition>
              </>
            ) : (
              <Definition term="Account">
                <Badge tone="outline">None</Badge>
                <span className="ml-2 text-tertiary">Cannot sign in</span>
              </Definition>
            )}
            <Definition term="Created">{formatDateTime(person.createdAt)}</Definition>
            {person.account?.deactivatedAt ? (
              <Definition term="Deactivated">
                {formatDateTime(person.account.deactivatedAt)}
              </Definition>
            ) : null}
            <Definition term="Identifier">
              <Mono>{person.id}</Mono>
            </Definition>
          </DefinitionList>

          {person.account?.status === 'invited' && canManage ? (
            <div className="mt-5 flex flex-col gap-2 border-t border-secondary pt-4">
              <p className="text-sm text-tertiary">
                Invited, not yet signed in. Status flips to active on their first successful
                sign-in.
              </p>
              <div className="flex items-center gap-3">
                <Button
                  size="sm"
                  variant="secondary"
                  isPending={resend.isPending}
                  onPress={() => resend.mutate()}
                >
                  Resend invitation
                </Button>
                {resend.isSuccess ? (
                  <span className="text-sm text-success-primary">
                    {resend.data.invitation.sent
                      ? 'Sent.'
                      : `Not sent: ${resend.data.invitation.error}`}
                  </span>
                ) : null}
              </div>
              {resend.isError ? <ErrorState error={resend.error} /> : null}
            </div>
          ) : null}
        </Card>

        <div className="flex flex-col gap-6">
          <Card padded={false}>
            <CardHeader
              title="Membership"
              description="Who they work for. This is not access — it grants nothing anywhere."
            />
            <div className="p-5">
              {memberships.length === 0 ? (
                <p className="text-sm text-tertiary">
                  No organization membership. Staff and contractors normally have none: Matchbox is
                  not an organization row, and organizations represent clients only.
                </p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {memberships.map((membership) => (
                    <li key={membership.organizationId} className="flex items-center gap-2 text-sm">
                      <Link
                        to="/organizations/$organizationId"
                        params={{ organizationId: membership.organizationId }}
                        className="font-medium text-primary no-underline hover:underline"
                      >
                        {membership.organizationName}
                      </Link>
                      {membership.title ? (
                        <span className="text-tertiary">— {membership.title}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>

          <Card padded={false}>
            <CardHeader
              title="Access"
              description="What they can actually reach. A separate edge, resolved from grants."
              actions={
                <LinkButton size="sm" variant="secondary" href={`/access/people/${person.id}`}>
                  Open the view
                </LinkButton>
              }
            />
            <div className="p-5">
              <p className="text-sm text-secondary">
                {grantCount === 0
                  ? 'No person-specific grants. They may still reach nodes through a role-principal grant — the effective-access view is what answers that.'
                  : `${grantCount} person-specific grant${grantCount === 1 ? '' : 's'}. Role-principal grants reach them too, and are not counted here.`}
              </p>
              {person.account === null && grantCount > 0 ? (
                <p className="mt-2 text-sm text-tertiary">
                  Those grants currently reach nobody. A grant names a person, and resolution starts
                  from their role — with no account there is no role, so nothing resolves. Giving
                  them access is what makes these take effect.
                </p>
              ) : null}
            </div>
          </Card>
        </div>
      </div>

      <Dialog title="Edit person" isOpen={editing} onOpenChange={setEditing}>
        {(close) => (
          <EditDialog
            personId={person.id}
            initial={{ name: person.name, email: person.email }}
            onClose={close}
          />
        )}
      </Dialog>

      <Dialog
        title="Give this person access"
        description="Creates their account and sends the invitation. One account per person."
        isOpen={givingAccess}
        onOpenChange={setGivingAccess}
      >
        {(close) => <GiveAccessDialog personId={person.id} name={person.name} onClose={close} />}
      </Dialog>
    </>
  );
}
